import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { WhatsappButton } from "@/components/site/WhatsappButton";

const links = [
  { label: "Sobre", href: "#sobre" },
  { label: "Diferenciais", href: "#diferenciais" },
  { label: "Avaliações", href: "#avaliacoes" },
  { label: "Localização", href: "#localizacao" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  // Ultra-lightweight scroll detection using RAF throttle
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 30);
          ticking = false;
        });
        ticking = true;
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  // Lock body scroll on mobile menu open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-400 ${
          scrolled
            ? "glass-nav border-b border-clinic-line/70 py-3 shadow-soft"
            : "bg-transparent py-5 md:py-6"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#top"
            data-testid="nav-logo"
            className="group flex flex-col items-start transition-opacity hover:opacity-90"
          >
            <span className="font-serif text-2xl md:text-[1.8rem] font-medium leading-none tracking-tight text-clinic-ink">
              Lília Targa
            </span>
            <span className="text-[0.62rem] font-sans font-medium tracking-[0.38em] uppercase text-clinic-rose mt-1 transition-colors group-hover:text-clinic-ink">
              Saúde & Beleza
            </span>
          </a>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-1.5 rounded-full border border-clinic-line/60 bg-white/40 p-1.5 backdrop-blur-md">
            {links.map((l) => {
              const isActive = activeSection === l.href.substring(1);
              return (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`relative px-4 py-1.5 rounded-full font-sans text-sm transition-all duration-300 ${
                    isActive
                      ? "text-clinic-ink font-semibold bg-white shadow-sm"
                      : "text-clinic-ink/75 hover:text-clinic-ink hover:bg-white/50 font-normal"
                  }`}
                >
                  {l.label}
                </a>
              );
            })}
          </nav>

          {/* CTA Right */}
          <div className="hidden md:flex items-center gap-3">
            <WhatsappButton size="md" variant="pink" testId="nav-whatsapp-cta" />
          </div>

          {/* Mobile Toggle Button */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            className="md:hidden relative z-50 flex h-11 w-11 items-center justify-center rounded-full border border-clinic-line bg-white/80 text-clinic-ink transition-transform active:scale-90"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </motion.header>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-30 md:hidden bg-clinic-ink/40 backdrop-blur-md"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 left-0 right-0 bg-clinic-bg border-b border-clinic-line pt-24 pb-8 px-6 shadow-soft-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col gap-3">
                {links.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    className="flex items-center justify-between py-3 border-b border-clinic-line/50 font-serif text-2xl text-clinic-ink active:text-clinic-rose"
                  >
                    <span>{l.label}</span>
                    <ArrowUpRight className="h-4 w-4 text-clinic-rose/70" />
                  </motion.a>
                ))}
                <div className="pt-4">
                  <WhatsappButton
                    className="w-full text-center"
                    testId="nav-mobile-whatsapp-cta"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

